import axios from 'axios';
import { config } from 'config/env';
import type { JusGptV2AccessResponse } from './types';

/**
 * Gate de acesso à interface 2.0 do JusGPT.
 *
 * Substituiu a allowlist de clientId que vivia chumbada no front: quem decide
 * agora é o backend, que já tem o user_id no JWT. Nada de user_id no request.
 *
 * O gate é fail-closed: só `data.enabled === true` libera. Erro, timeout, corpo
 * fora do formato ou serviço fora do ar caem na interface antiga, que funciona.
 * O advogado não tem o que fazer com uma mensagem de erro aqui.
 */

const CAMINHO = '/api/jusgpt/v2-access';

/**
 * O default do axios é 1 minuto (ver src/redux/setupAxios.js). É muito para um
 * gate de UI: até responder, o menu inteiro fica esperando para saber qual
 * interface abrir. Oito segundos é o teto — depois disso vale mais entregar a
 * interface antiga do que continuar esperando.
 */
const TIMEOUT_MS = 8000;

export async function fetchJusGptV2Access(token: string): Promise<boolean> {
  // A leitura é opcional de propósito: REACT_APP_JUSGPT_API é `.optional()` no
  // schema da env, e config incompleto (um mock parcial num spec) não pode
  // derrubar o menu — desliga o gate.
  const base = config.jusGpt?.apiUrl;
  if (!base) return false;

  // Sem token a resposta é 401 garantido. Não vale o round-trip.
  if (!token) return false;

  const url = `${base.replace(/\/+$/, '')}${CAMINHO}`;

  /*
    Instância própria, e não o axios global, por causa do interceptor de
    resposta do setupAxios: ele manda a sessão pro /logout em QUALQUER 401. Um
    401 deste gate — token que acabou de expirar, serviço mal configurado —
    derrubaria o login do advogado por causa de uma consulta de menu. Instância
    criada por `axios.create` não herda interceptor do default.

    O Bearer, por consequência, vai à mão: o interceptor de request que injeta o
    header também mora no global.
  */
  const http = axios.create({
    timeout: TIMEOUT_MS,
    headers: { Authorization: `Bearer ${token}` },
  });

  try {
    const { data } = await http.get<JusGptV2AccessResponse>(url);
    return data?.data?.enabled === true;
  } catch (_) {
    return false;
  }
}
